import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronDown, ChevronUp, User, Package, Bell, RefreshCw, DollarSign, Mail, Phone, MapPin, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LockerManagement = ({ token, clients }) => {
  const [mailboxes, setMailboxes] = useState([]);
  const [expandedLocker, setExpandedLocker] = useState(null);
  const [trackings, setTrackings] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [repacks, setRepacks] = useState([]);
  const [shippingRates, setShippingRates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [mailboxesRes, trackingsRes, alertsRes, repacksRes, ratesRes] = await Promise.all([
        axios.get(`${API}/admin/mailboxes`, axiosConfig),
        axios.get(`${API}/admin/trackings`, axiosConfig),
        axios.get(`${API}/admin/alerts`, axiosConfig),
        axios.get(`${API}/admin/repacks`, axiosConfig),
        axios.get(`${API}/admin/shipping-rates`, axiosConfig)
      ]);

      setMailboxes(mailboxesRes.data);
      setTrackings(trackingsRes.data);
      setAlerts(alertsRes.data);
      setRepacks(repacksRes.data);
      setShippingRates(ratesRes.data);
    } catch (error) {
    }
  };

  const getClientInfo = (clientId) => {
    return clients.find(c => c.id === clientId) || {};
  };

  const getClientTrackings = (clientId) => {
    return trackings.filter(t => t.client_id === clientId);
  };

  const getClientAlerts = (clientId) => {
    return alerts.filter(a => a.client_id === clientId);
  };

  const getClientRepacks = (clientId) => {
    return repacks.filter(r => r.client_id === clientId);
  };

  const handleAssignRate = async (mailboxId, rateId) => {
    try {
      await axios.put(
        `${API}/admin/mailboxes/${mailboxId}`,
        { shipping_rate_id: rateId },
        axiosConfig
      );
      alert('✅ Tarifa asignada exitosamente');
      fetchAllData();
    } catch (error) {
      alert('Error al asignar tarifa: ' + error.message);
    }
  };

  const toggleLocker = (lockerId) => {
    setExpandedLocker(expandedLocker === lockerId ? null : lockerId);
  };

  const filteredMailboxes = mailboxes.filter(mailbox => {
    const client = getClientInfo(mailbox.client_id);
    const matchesSearch = searchTerm === '' || 
      client.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mailbox.locker_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'todos' || 
      (filterStatus === 'activos' && client.is_active) ||
      (filterStatus === 'inactivos' && !client.is_active);
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Gestión de Casilleros ({filteredMailboxes.length})</CardTitle>
          <CardDescription>Administra casilleros, asigna tarifas y revisa actividades</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Buscar</Label>
              <Input
                placeholder="Nombre, email, casillero..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="activos">Activos</SelectItem>
                  <SelectItem value="inactivos">Inactivos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lockers List */}
      <div className="space-y-3">
        {filteredMailboxes.map((mailbox) => {
          const client = getClientInfo(mailbox.client_id);
          const clientTrackings = getClientTrackings(mailbox.client_id);
          const clientAlerts = getClientAlerts(mailbox.client_id);
          const clientRepacks = getClientRepacks(mailbox.client_id);
          const isExpanded = expandedLocker === mailbox.id;
          const assignedRate = shippingRates.find(r => r.id === mailbox.shipping_rate_id);

          return (
            <Card key={mailbox.id} className={`${isExpanded ? 'border-2 border-blue-400' : ''}`}>
              <CardContent className="p-0">
                {/* Header - Always Visible */}
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleLocker(mailbox.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <User className="h-5 w-5 text-blue-600" />
                        <h3 className="font-bold text-lg">{client.full_name || 'Sin nombre'}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          client.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {client.is_active ? '✓ Activo' : '✗ Inactivo'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-gray-500" />
                          <div>
                            <p className="text-gray-600 text-xs">Casillero</p>
                            <p className="font-semibold">{mailbox.locker_id}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-500" />
                          <div>
                            <p className="text-gray-600 text-xs">Email</p>
                            <p className="font-semibold text-xs">{client.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <div>
                            <p className="text-gray-600 text-xs">Teléfono</p>
                            <p className="font-semibold">{client.phone_country_code} {client.phone || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <div>
                            <p className="text-gray-600 text-xs">Registro</p>
                            <p className="font-semibold text-xs">
                              {new Date(client.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Quick Stats */}
                      <div className="flex gap-4 mt-3">
                        <div className="flex items-center gap-1">
                          <Package className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-semibold">{clientTrackings.length} Paquetes</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Bell className="h-4 w-4 text-yellow-600" />
                          <span className="text-sm font-semibold">{clientAlerts.length} Alertas</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <RefreshCw className="h-4 w-4 text-purple-600" />
                          <span className="text-sm font-semibold">{clientRepacks.length} Repacks</span>
                        </div>
                      </div>
                    </div>

                    <Button variant="ghost" size="sm">
                      {isExpanded ? <ChevronUp /> : <ChevronDown />}
                    </Button>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t bg-gray-50 p-4 space-y-4">
                    {/* Client Details */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-white p-4 rounded-lg border">
                      <div>
                        <p className="text-xs text-gray-600">Tipo de Cuenta</p>
                        <p className="font-semibold">{client.account_type || 'Persona'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Documento</p>
                        <p className="font-semibold">{client.id_document || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Ciudad</p>
                        <p className="font-semibold">{client.city || 'N/A'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-gray-600">Dirección</p>
                        <p className="font-semibold">{client.address || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Assign Rate */}
                    <div className="bg-white p-4 rounded-lg border">
                      <div className="flex items-center gap-2 mb-3">
                        <DollarSign className="h-5 w-5 text-green-600" />
                        <h4 className="font-semibold">Tarifa Asignada</h4>
                      </div>
                      <div className="flex gap-3">
                        <Select 
                          value={mailbox.shipping_rate_id || 'no-asignada'}
                          onValueChange={(v) => v !== 'no-asignada' && handleAssignRate(mailbox.id, v)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Seleccionar tarifa" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="no-asignada">Sin tarifa asignada</SelectItem>
                            {shippingRates.map(rate => (
                              <SelectItem key={rate.id} value={rate.id}>
                                {rate.shipping_type} - {rate.customer_type} (${rate.rate_per_unit}/{rate.unit})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {assignedRate && (
                          <div className="px-4 py-2 bg-green-50 border border-green-200 rounded">
                            <p className="text-xs text-green-700">Tarifa Actual</p>
                            <p className="font-bold text-green-800">
                              ${assignedRate.rate_per_unit}/{assignedRate.unit}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Alerts Section */}
                    <div className="bg-white p-4 rounded-lg border">
                      <div className="flex items-center gap-2 mb-3">
                        <Bell className="h-5 w-5 text-yellow-600" />
                        <h4 className="font-semibold">Alertas de Paquetes ({clientAlerts.length})</h4>
                      </div>
                      {clientAlerts.length > 0 ? (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {clientAlerts.map(alert => (
                            <div key={alert.id} className="border p-3 rounded text-sm bg-yellow-50">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="font-semibold">{alert.tracking_number}</p>
                                  <p className="text-xs text-gray-600">{alert.description}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Tienda: {alert.store_origin || 'N/A'} | Peso est: {alert.estimated_weight} lb
                                  </p>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                  alert.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                                  alert.status === 'acknowledged' ? 'bg-blue-200 text-blue-800' :
                                  'bg-green-200 text-green-800'
                                }`}>
                                  {alert.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No hay alertas</p>
                      )}
                    </div>

                    {/* Repack Requests */}
                    <div className="bg-white p-4 rounded-lg border">
                      <div className="flex items-center gap-2 mb-3">
                        <RefreshCw className="h-5 w-5 text-purple-600" />
                        <h4 className="font-semibold">Solicitudes de Reempaque ({clientRepacks.length})</h4>
                      </div>
                      {clientRepacks.length > 0 ? (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {clientRepacks.map(repack => {
                            const repackTrackings = trackings.filter(t => repack.tracking_ids.includes(t.id));
                            return (
                              <div key={repack.id} className="border-2 p-3 rounded bg-purple-50 border-purple-200">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <p className="font-semibold text-sm">
                                      {repack.tracking_ids.length} paquetes - {repack.shipping_method}
                                    </p>
                                    <p className="text-xs text-gray-600 mt-1">
                                      Solicitado: {new Date(repack.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                    repack.status === 'requested' ? 'bg-orange-200 text-orange-900' :
                                    repack.status === 'processing' ? 'bg-blue-200 text-blue-900' :
                                    'bg-green-200 text-green-900'
                                  }`}>
                                    {repack.status === 'requested' ? 'Pendiente' :
                                     repack.status === 'processing' ? 'En Proceso' : 'Completado'}
                                  </span>
                                </div>
                                <div className="bg-white p-2 rounded border text-xs">
                                  <p className="font-semibold mb-1">Trackings:</p>
                                  {repackTrackings.map(t => (
                                    <p key={t.id} className="font-mono text-xs">• {t.tracking_number}</p>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No hay solicitudes de reempaque</p>
                      )}
                    </div>

                    {/* Active Packages */}
                    <div className="bg-white p-4 rounded-lg border">
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="h-5 w-5 text-blue-600" />
                        <h4 className="font-semibold">Paquetes Activos ({clientTrackings.length})</h4>
                      </div>
                      {clientTrackings.length > 0 ? (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {clientTrackings.slice(0, 10).map(tracking => (
                            <div key={tracking.id} className="border p-2 rounded text-xs">
                              <div className="flex justify-between">
                                <div>
                                  <p className="font-semibold font-mono">{tracking.tracking_number}</p>
                                  <p className="text-gray-600">{tracking.description || 'Sin descripción'}</p>
                                </div>
                                <div className="text-right">
                                  <p className={`px-2 py-1 rounded text-xs font-semibold ${
                                    tracking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    tracking.status === 'in_warehouse' ? 'bg-blue-100 text-blue-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {tracking.status}
                                  </p>
                                  <p className="text-xs text-gray-600 mt-1">{tracking.weight || 0} lb</p>
                                </div>
                              </div>
                            </div>
                          ))}
                          {clientTrackings.length > 10 && (
                            <p className="text-xs text-gray-500 text-center">
                              ... y {clientTrackings.length - 10} más
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No hay paquetes activos</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {filteredMailboxes.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <User className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No se encontraron casilleros</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LockerManagement;
